<template>
  <div id="map" class="map-container">
    <HikeTrailsPanel
      v-if="!isMobile && showTrailsPanelComputed && plannedTrails && plannedTrails.length > 0"
      :hike-name="hikeName"
      :planned-trails="plannedTrails"
      :hike-days="hikeDays"
      :collapsed="trailsPanelCollapsed"
      :active-view-prop="activeView"
      @update:collapsed="trailsPanelCollapsed = $event"
      @trail-selected="onTrailSelected"
      @view-changed="onViewChanged"
      @show-options="onShowOptions"
    />
    <transition name="fade">
      <div
        v-if="isMobile && (showMobileTrailsComputed || showMobileElevationComputed)"
        class="mobile-scrim"
        @click="closeMobilePanels"
      ></div>
    </transition>
    <transition name="mobile-sheet">
      <div
        v-if="isMobile && showMobileTrailsComputed && plannedTrails && plannedTrails.length > 0"
        class="mobile-sheet mobile-sheet--routes"
      >
        <HikeTrailsPanel
          class="mobile-sheet__panel"
          :hike-name="hikeName"
          :planned-trails="plannedTrails"
          :hike-days="hikeDays"
          :collapsed="false"
          :active-view-prop="activeView"
          @update:collapsed="handleMobileTrailsCollapse"
          @trail-selected="onTrailSelected"
          @view-changed="onViewChanged"
          @show-options="onShowOptions"
        />
      </div>
    </transition>
    <transition name="mobile-sheet">
      <div
        v-if="isMobile && showMobileElevationComputed && geojson"
        class="mobile-sheet mobile-sheet--elevation"
      >
        <div class="mobile-sheet__header">
          <h3 class="mobile-sheet__title">{{ mobileElevationTitle }}</h3>
          <button class="mobile-sheet__close" type="button" @click="toggleMobileElevation(false)">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="mobile-sheet__content">
          <HikeMapElevationProfile
            :geojson="geojson"
            :active-view="activeView"
            :planned-trails="plannedTrails"
            :hike-days="hikeDays"
          />
        </div>
      </div>
    </transition>
    <HikeMapElevationProfile
      v-if="!isMobile && showElevationProfileComputed && geojson"
      :geojson="geojson"
      :active-view="activeView"
      :planned-trails="plannedTrails"
      :hike-days="hikeDays"
    />
    <HikeMeasurementPanel v-if="isActive" context="hike" />
    <MapControls
      :fit-bounds="controlsBounds"
      :fit-bounds-padding="controlsBoundsPadding"
    />
    <MapButtons :button-clicked="buttonTapped" :buttons="buttons"/>
    <TrailDataCard/>
    <WaypointAddingNotification :enabled="waypointAddingModeEnabled"/>
    <BaseMapStylesDialog />
    <GoToLocationDialog />
    <WaypointDialog />
    <EditRecordingDetails />
    <LoadingDialog :show-loading-dialog="loading" />
    <MapDataPOIDetailDialog/>
    <HighlightDetailDialog />
    <MapOptionsDialog
        @edit="editHike"
        @waypoints="openWaypoints"
        @goto="showGoTo"
        @print="openPrintMap"
        @measurement="showMeasurement"
        @report-issue="reportIssue"
        @create="createNewHike"
        :edit-hike="Boolean(editHikeUrl)"
        :show-create="true"
        :show-waypoints="true"
        :show-go-to="true"
        :show-measurement="true"
        :show-print="true"
        :show-report-issue="true"
      />
    <div v-if="isMobile" class="mobile-action-bar">
      <button
        v-if="plannedTrails && plannedTrails.length > 0"
        type="button"
        class="mobile-action-btn"
        :class="{ active: showMobileTrails }"
        @click="toggleMobileTrails()"
      >
        <span class="material-icons">{{ plannedTrails.length > 1 ? 'menu' : 'info' }}</span>
        <span>{{ plannedTrails.length > 1 ? 'Routes' : 'Details' }}</span>
      </button>
      <button
        type="button"
        class="mobile-action-btn"
        :class="{ active: showMobileElevation }"
        :disabled="!geojson"
        @click="toggleMobileElevation()"
      >
        <span class="material-icons">show_chart</span>
        <span>Elevation</span>
      </button>
    </div>
    <div class="bottom-controls-container">
      <div v-if="miniCoordsBar" class="coordsbar">
        <span class="material-icons">my_location</span> {{ miniCoordsBar }}
      </div>
      <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="currentLatitude" />
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { mapGetters } from 'vuex';
import BaseMapStylesDialog from '../../apps/./base_map_dialog/BaseMapStylesDialog.vue';
import MapButtons from '../../apps/components/MapButtons.vue';
import MapControls from '../../apps/components/MapControls.vue';
import EditRecordingDetails from '../../apps/edit_recording_details/EditRecordingDetails.vue';
import TrailDataCard from '../../apps/trail_data_card/TrailDataCard.vue';
import WaypointAddingNotification from "../../apps/waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import GoToLocationDialog from "../go_to_location_dialog/GoToLocationDialog.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import HikeTrailsPanel from './HikeTrailsPanel.vue';
import HikeMapElevationProfile from './HikeMapElevationProfile.vue';
import HikeMeasurementPanel from './components/HikeMeasurementPanel.vue';
import graph_client from '../../utils/graphql/graph_client';
import { GetRecordedTrailById } from '../../utils/graphql/recorded-trail-graph';
import { HiikerMapStyle, ThreeDimensionalMapManager } from '../../utils/models/MapStyles';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from 'bootstrap';
import MapOptionsDialog from '../map_options_dialog/MapOptionsDialog.vue';
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";
import emitter from "../../utils/bus";
import {LayerRegistry, MapLayerCategory} from "../../utils/layer-registry";
import {GetHike} from "../../utils/graphql/hikes-graph";
import { ROUTE_STYLES, DEFAULT_ROUTE_STYLE } from "../../utils/constants/route-styles";
import { getRouteStyleSettings, buildRouteStyleExpressions } from "../../utils/routeStyleSettings";
import { hydrateFromServer as hydrateRouteStyleFromServer } from "../../utils/routeStyleSettingsCloudSync";
import { POIUtility } from "../../utils/models/POI";
import HighlightDetailDialog from "./components/HighlightDetailDialog.vue";
import { geojsonToGpx } from "../../utils/geojsonToGpx";
import { openPrintMap as openPrintMapUtil } from "../../utils/printMaps";
import ScaleBar from "../../apps/components/ScaleBar.vue";
import {GridRefUtils} from "../../utils/helpers/GridRefPreference";
import { computeHikeMapFitBounds } from "../../utils/helpers/hikeMapFitBounds";
import {IndexedDBCache} from "../../utils/crosshair_altitude_service/indexed_db_cache";
import {getElevation} from "../../utils/crosshair_altitude_service/pixel_methods";
import {throttle} from 'lodash-es';
import { bearing as turfBearing, distance as turfDistance } from '@turf/turf';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';


export default {
  name: 'RecordingMap',
  components: {
    LoadingDialog,
    EditRecordingDetails,
    BaseMapStylesDialog,
    MapOptionsDialog,
    MapButtons,
    MapControls,
    TrailDataCard,
    WaypointAddingNotification,
    GoToLocationDialog,
    WaypointDialog,
    MapDataPOIDetailDialog,
    HikeTrailsPanel,
    HikeMapElevationProfile,
    HighlightDetailDialog,
    ScaleBar,
    HikeMeasurementPanel
  },
  data() {
    return {
      loading: true,
      buttons: [
        {id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true,},
        {id: 1, name: 'Options', icon: 'add', enabled: false, dropIcon: false}, // Initial state depends on map state
      ],
      editHikeUrl: null,
      isHikeOwner: false,
      geojson: null,
      hikeName: '',
      bbox: [],
      plannedTrails: [],
      hikeDays: [],
      hikeHighlights: [],
      trailType: null,
      showHighlights: (typeof window !== 'undefined' && window.hikeHighlightsVisible !== undefined)
        ? !!window.hikeHighlightsVisible
        : true,
      showTrailsPanel: true,
      trailsPanelCollapsed: false,
      showElevationProfile: true,
      selectedTrailIndex: null,
      activeView: { type: 'full' },
      mapInstance: null,
      currentLatitude: 0,
      waypointAddingModeEnabled: false,
      miniCoordsBar: null,
      mouseMoveHandler: null,
      isMobile: false,
      showMobileTrails: false,
      showMobileElevation: false,
      desktopElevationPreference: true,
      activeBounds: null,
      hoverMarker: null, // Marker for elevation profile hover
    };
  },
  computed: {
    ...mapGetters('hikeMeasurement', ['isActive', 'hoverCoordinate']),

    mobileElevationTitle() {
      if (this.activeView?.type === 'day') {
        const dayInfo = this.hikeDays?.find?.((d) => d.dayNumber === this.activeView.dayNumber);
        return dayInfo?.name || `Day ${this.activeView.dayNumber}`;
      }

      if (this.activeView?.type === 'route') {
        const targetId = this.activeView?.trailId != null ? String(this.activeView.trailId) : null;
        if (targetId) {
          const trail = this.plannedTrails?.find?.((candidate) => {
            const candidateId = candidate?.id ?? candidate?.ptv2PlannedTrail?.id;
            return candidateId != null && String(candidateId) === targetId;
          });
          const title = trail?.title ?? trail?.ptv2PlannedTrail?.title;
          if (title) return title;
        }
        return 'Route';
      }

      return this.hikeName || 'Elevation';
    },
    defaultMapBounds() {
      if (Array.isArray(this.bbox) && this.bbox.length === 4) {
        return [
          [this.bbox[0], this.bbox[1]],
          [this.bbox[2], this.bbox[3]],
        ];
      }
      if (window.lonlat && Array.isArray(window.lonlat)) {
        const [lng, lat] = window.lonlat;
        return [[lng - 0.1, lat - 0.1], [lng + 0.1, lat + 0.1]];
      }
      return [[-180, -85], [180, 85]];
    },
    controlsBounds() {
      return this.activeBounds || this.defaultMapBounds;
    },
    controlsBoundsPadding() {
      return this.isMobile ? 60 : 150;
    },

    // Show panels only when measurement tool is not active
    showTrailsPanelComputed() {
      return this.showTrailsPanel && !this.isActive;
    },

    showElevationProfileComputed() {
      return this.showElevationProfile && !this.isActive;
    },

    showMobileTrailsComputed() {
      return this.showMobileTrails && !this.isActive;
    },

    showMobileElevationComputed() {
      return this.showMobileElevation && !this.isActive;
    }
  },
  watch: {
    hoverCoordinate(newCoord, oldCoord) {
      this.updateHoverMarker(newCoord);
    }
  },
  async mounted() {
    this.handleViewportChange();
    window.addEventListener('resize', this.handleViewportChange);
    emitter.on('hike-highlights:toggle', this.handleHighlightToggle);
    // Live re-apply the customise-route-style settings whenever they change.
    // CustomiseRouteStyleView emits this on every persist; the matching
    // listener in routeStyleSettingsCloudSync schedules the GraphQL push.
    emitter.on('route-style-settings:changed', this.applyRouteStyleToHikeLines);
    // Pull latest server route-style on every hike-map mount so a colour
    // edited on Android/iOS lands without a full page reload.
    void hydrateRouteStyleFromServer();

    // Fetch data first before initializing map
    await this.fetchData();

    // Now initialize map with proper bounds from fetched data
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
    const isPro = await RevenueCatWrapper.isProOrProPlus();
    const mapStyle = HiikerMapStyle.getCurrentStyle(isPro);
    
    // Calculate initial center and bounds from bbox if available
    let mapOptions = {
      container: 'map',
      style: mapStyle.styleUrl,
    };
    
    if (this.bbox && this.bbox.length === 4) {
      // Use bbox to set initial bounds
      mapOptions.bounds = [
        [this.bbox[0], this.bbox[1]], // southwest corner [min_lon, min_lat]
        [this.bbox[2], this.bbox[3]]  // northeast corner [max_lon, max_lat]
      ];
      mapOptions.fitBoundsOptions = { padding: 300 };
    } else if (window.lonlat) {
      // Fallback to window.lonlat if no bbox
      mapOptions.center = window.lonlat;
      mapOptions.zoom = 12;
    } else {
      // Default fallback
      mapOptions.center = [0, 0];
      mapOptions.zoom = 12;
    }
    
    window.map = new mapboxgl.Map(mapOptions);
    this.mapInstance = window.map; // Store instance locally for scale bar
    this.currentLatitude = window.map.getCenter().lat; // Initialize latitude
    window.layerRegistry = new LayerRegistry(window.map);

    // Initialize waypoints store module to set up click handlers
    if (this.$store) {
      await this.$store.dispatch('waypoints/initialiseStore');
    }

    // Handle resize events to ensure the map fills the screen
    window.addEventListener('resize', this.resizeMap);
    this.resizeMap();

    // Add a map on load
    window.map.on('load', () => {
      emitter.emit('map-loaded');
      this.addToMap();

      // Add mouse move listener for coordinate bar
      const gridRef = GridRefUtils.preference();
      const cache = new IndexedDBCache();
      const handleMouseMove = async (e) => {
        const coords = gridRef.convert(e.lngLat);
        const currentZoom = window.map.getZoom();
        const elevation = await getElevation(e.lngLat, currentZoom, cache);
        this.miniCoordsBar = `${coords.toString()} ${elevation}`;
      };
      this.mouseMoveHandler = throttle(handleMouseMove, 50);
      window.map.on('mousemove', this.mouseMoveHandler);
    });
    window.map.on('style.load', () => {
      emitter.emit('map-style-loaded');
      // Re-add layers after a style switch, but preserve the user's current
      // camera — changing the base map style must not re-zoom to the hike bounds.
      this.addToMap({ fitBounds: false });
      this.addHighlightsToMap();
      // Re-add measurement line if measurement mode is active
      this.$store.dispatch('hikeMeasurement/readdMeasurementToMap');
    });

    // Update latitude on map move for accurate scale calculations
    window.map.on('move', () => {
      this.currentLatitude = window.map.getCenter().lat;
    });

    // Some modules re-add their layers after emitting this event. Listening
    // to it ensures our trail line is restored if other handlers removed it
    // when the style changed.
    emitter.on('map-style-loaded', () => {
      // Same as the style.load handler: re-add layers without re-fitting bounds
      // so a style change keeps the user's current map position and zoom.
      this.addToMap({ fitBounds: false });
      this.addHighlightsToMap();
    });
  },
  methods: {
    resizeMap() {
      // Ensure map resizes when window size changes
      if (window.map) {
        window.map.resize();
      }
    },
    handleViewportChange() {
      const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

      if (isMobileViewport) {
        if (!this.isMobile) {
          this.desktopElevationPreference = this.showElevationProfile;
          this.trailsPanelCollapsed = true;
          this.showMobileTrails = false;
          this.showMobileElevation = false;
          this.showElevationProfile = false;
        }
      } else if (this.isMobile) {
        this.showElevationProfile = this.desktopElevationPreference && !!this.geojson;
        this.trailsPanelCollapsed = false;
        this.showMobileTrails = false;
        this.showMobileElevation = false;
      }

      this.isMobile = isMobileViewport;
    },
    toggleMobileTrails(force) {
      if (!this.isMobile) return;
      const nextState = typeof force === 'boolean' ? force : !this.showMobileTrails;
      this.showMobileTrails = nextState;
      if (nextState) {
        this.showMobileElevation = false;
      }
    },
    toggleMobileElevation(force) {
      if (!this.isMobile) return;
      const nextState = typeof force === 'boolean' ? force : !this.showMobileElevation;
      if (nextState && !this.geojson) {
        return;
      }
      this.showMobileElevation = nextState;
      if (nextState) {
        this.showMobileTrails = false;
      }
    },
    closeMobilePanels() {
      if (!this.isMobile) return;
      this.showMobileTrails = false;
      this.showMobileElevation = false;
    },
    handleMobileTrailsCollapse(collapsed) {
      if (collapsed) {
        this.showMobileTrails = false;
      }
    },
    async buttonTapped(button) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      switch (button) {
        case 0:
          // Map Layers
          if (window.user) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles);
          }
        break;
        case 1:
          // 3D maps
          this.closeMobilePanels();
          const optionsEl = document.getElementById('map-options-dialog');
          if (optionsEl) Modal.getOrCreateInstance(optionsEl).show();
        break;
        default:
        break;
      }
    },
    async fetchData() {
      const response = await graph_client.request(GetHike, { id: `${window.hike_id}` });
      const { hike } = response;
      if (!hike) {
        console.error('[HikeMap] Hike not found for id:', window.hike_id);
        return;
      }
      const geojson = JSON.parse(hike.geojsonMultilinestringsWithStartEndPointsString);
      this.geojson = geojson;
      this.hikeName = hike.name;
      this.bbox = geojson.bbox;
      // Use hikesPlannedTrails if available (new structure with day info), 
      // otherwise fall back to ptv2PlannedTrails for backward compatibility
      this.plannedTrails = hike.hikesPlannedTrails || hike.ptv2PlannedTrails || [];
      this.hikeDays = hike.hikeDays || [];
      this.hikeHighlights = hike.hikeHighlights || [];
      this.trailType = hike.trailType || null;
      this.isHikeOwner = !!(
        window.user?.firebase_user_id &&
        hike.user?.firebaseUserId &&
        window.user.firebase_user_id === hike.user.firebaseUserId
      );
      this.maybeEnableEditButton(hike);

      if (!this.isMobile && this.desktopElevationPreference) {
        this.showElevationProfile = true;
      }
    },
    isPrimaryRoute(routeStyle) {
      return !routeStyle || routeStyle === 'route';
    },
    buildTrailOrderMap() {
      const map = new Map();
      [...this.plannedTrails]
        .sort((a, b) => ((a.day ?? 0) - (b.day ?? 0)) || ((a.position ?? 0) - (b.position ?? 0)))
        .forEach((t, idx) => {
          const id = t.ptv2PlannedTrail?.id ?? t.id;
          if (id != null) map.set(String(id), idx);
        });
      return map;
    },
    sortFeaturesByTrailOrder(features) {
      const orderMap = this.buildTrailOrderMap();
      if (orderMap.size === 0) return features;
      return [...features].sort((a, b) => {
        const aIdx = orderMap.get(String(a.properties?.ptv2PlannedTrailId)) ?? 9999;
        const bIdx = orderMap.get(String(b.properties?.ptv2PlannedTrailId)) ?? 9999;
        return aIdx - bIdx;
      });
    },
    buildMarkerLookup(features) {
      const lookup = {
        start: Object.create(null),
        end: Object.create(null),
      };

      features.forEach((feature) => {
        if (feature.geometry?.type !== 'Point') {
          return;
        }

        const props = feature.properties || {};
        const trailId = props.ptv2PlannedTrailId;
        if (trailId == null || !props.markerType) {
          return;
        }

        const key = String(trailId);
        if (props.markerType === 'start') {
          lookup.start[key] = feature;
        } else if (props.markerType === 'end') {
          lookup.end[key] = feature;
        }
      });

      return lookup;
    },
    findMarkerInAllFeatures(trailId, markerType, expectedDayNumber = null) {
      if (!this.geojson || !Array.isArray(this.geojson.features)) {
        return null;
      }

      const targetId = String(trailId);

      return this.geojson.features.find((feature) => {
        if (feature.geometry?.type !== 'Point') {
          return false;
        }

        const props = feature.properties || {};
        if (!props.markerType || props.markerType !== markerType) {
          return false;
        }

        if (props.ptv2PlannedTrailId == null || String(props.ptv2PlannedTrailId) !== targetId) {
          return false;
        }

        if (expectedDayNumber != null && props.hikeDayNumber != null) {
          return props.hikeDayNumber === expectedDayNumber;
        }

        return true;
      }) || null;
    },
    getStartEndMarkers(filteredFeatures) {
      const result = { startMarkers: [], endMarkers: [] };
      if (!filteredFeatures || filteredFeatures.length === 0) {
        return result;
      }

      const markerLookup = this.buildMarkerLookup(filteredFeatures);
      const getMarker = (trailId, markerType, expectedDayNumber = null) => {
        if (trailId == null) {
          return null;
        }
        const key = String(trailId);
        let marker = null;
        if (markerType === 'start') {
          marker = markerLookup.start[key];
        } else if (markerType === 'end') {
          marker = markerLookup.end[key];
        }

        if (!marker) {
          marker = this.findMarkerInAllFeatures(trailId, markerType, expectedDayNumber);
        }

        return marker || null;
      };

      if (this.activeView.type === 'route') {
        const routeId = this.activeView.trailId;
        const startMarker = getMarker(routeId, 'start');
        if (startMarker) {
          result.startMarkers.push(startMarker);
        }
        const endMarker = getMarker(routeId, 'end');
        if (endMarker) {
          result.endMarkers.push(endMarker);
        }
        return result;
      }

      const routeLineFeatures = filteredFeatures.filter((feature) => {
        return feature.geometry?.type === 'MultiLineString' &&
          this.isPrimaryRoute(feature.properties?.routeStyle);
      });

      if (routeLineFeatures.length === 0) {
        return result;
      }

      const firstRoute = routeLineFeatures[0];
      const lastRoute = routeLineFeatures[routeLineFeatures.length - 1];

      const firstRouteId = firstRoute?.properties?.ptv2PlannedTrailId;
      const lastRouteId = lastRoute?.properties?.ptv2PlannedTrailId;

      const firstStartMarker = getMarker(firstRouteId, 'start', firstRoute?.properties?.hikeDayNumber);
      if (firstStartMarker) {
        result.startMarkers.push(firstStartMarker);
      }

      const lastEndMarker = getMarker(lastRouteId, 'end', lastRoute?.properties?.hikeDayNumber);
      if (lastEndMarker) {
        result.endMarkers.push(lastEndMarker);
      }

      return result;
    },
    deriveBoundsFromFeatures(features) {
      if (!Array.isArray(features) || features.length === 0) {
        return null;
      }

      const bounds = new mapboxgl.LngLatBounds();
      let hasValidCoordinate = false;

      const extendCoord = (coord) => {
        if (!Array.isArray(coord) || coord.length < 2) return;
        const [lng, lat] = coord;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          bounds.extend([lng, lat]);
          hasValidCoordinate = true;
        }
      };

      features.forEach((feature) => {
        if (!feature?.geometry) return;
        const { type, coordinates } = feature.geometry;

        if (!coordinates) return;

        if (type === 'Point') {
          extendCoord(coordinates);
        } else if (type === 'MultiPoint' || type === 'LineString') {
          coordinates.forEach(extendCoord);
        } else if (type === 'MultiLineString') {
          coordinates.forEach((line) => {
            if (Array.isArray(line)) {
              line.forEach(extendCoord);
            }
          });
        }
      });

      if (!hasValidCoordinate) {
        return null;
      }

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      return [[sw.lng, sw.lat], [ne.lng, ne.lat]];
    },
    buildDistanceMarkerFeatures(multiLineStrings) {
      const pref = MeasurementPrefUtility.preference();
      const isMetric = pref.isMetric();
      const unitLabel = isMetric ? 'km' : 'mi';
      const kmPerUnit = isMetric ? 1 : 1.609344;

      // Walk one ordered batch of features and emit a marker every 0.5 units
      // along that batch's combined geometry, restarting from 0 each call.
      // Whole-unit positions get a label string; halves stay chevron-only.
      // Each marker captures the local line bearing so its chevron rotates
      // to point in the direction of travel.
      const buildForBatch = (batch) => {
        const out = [];
        let cumulativeKm = 0;
        let nextHalfStep = 1;
        batch.forEach((feature) => {
          const lines = feature?.geometry?.coordinates || [];
          lines.forEach((coords) => {
            if (!Array.isArray(coords) || coords.length < 2) return;
            // Walk segment-by-segment so each marker is O(1) to place.
            // turfAlong scans from the line start on every call, which was
            // O(n²) for hikes stored as a single massive coordinate array.
            let lineKm = 0;
            for (let i = 0; i < coords.length - 1; i++) {
              const from = coords[i];
              const to = coords[i + 1];
              const segKm = turfDistance(from, to, { units: 'kilometers' });
              const segStart = cumulativeKm + lineKm;
              const segEnd = segStart + segKm;
              while ((nextHalfStep * 0.5) * kmPerUnit <= segEnd) {
                const targetKm = (nextHalfStep * 0.5) * kmPerUnit;
                const t = segKm > 0 ? (targetKm - segStart) / segKm : 0;
                const lng = from[0] + t * (to[0] - from[0]);
                const lat = from[1] + t * (to[1] - from[1]);
                const bearing = turfBearing([from[0], from[1]], [to[0], to[1]]);
                const isWhole = nextHalfStep % 2 === 0;
                out.push({
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: [lng, lat] },
                  properties: {
                    distance: nextHalfStep * 0.5,
                    unit: unitLabel,
                    label: isWhole ? `${nextHalfStep / 2} ${unitLabel}` : '',
                    isWhole,
                    bearing,
                  },
                });
                nextHalfStep += 1;
              }
              lineKm += segKm;
            }
            cumulativeKm += lineKm;
          });
        });
        return out;
      };

      // Main route accumulates across all primary features; each alt / spur
      // is its own counter keyed by ptv2PlannedTrailId. Transport features
      // don't get markers since they aren't part of the hike's distance.
      const primary = [];
      const branchesByTrailId = new Map();
      multiLineStrings.forEach((feature) => {
        const style = feature?.properties?.routeStyle;
        if (style === 'transport') return;
        if (this.isPrimaryRoute(style)) {
          primary.push(feature);
          return;
        }
        const key = String(feature.properties?.ptv2PlannedTrailId ?? `__branch_${branchesByTrailId.size}`);
        if (!branchesByTrailId.has(key)) branchesByTrailId.set(key, []);
        branchesByTrailId.get(key).push(feature);
      });

      const features = [];
      features.push(...buildForBatch(primary));
      branchesByTrailId.forEach((batch) => {
        features.push(...buildForBatch(batch));
      });

      return features;
    },
    // Re-applies the user's current customise-route-style preferences to the
    // four hike-line layers without rebuilding them. Called on
    // `route-style-settings:changed` so a color/thickness/opacity tweak in the
    // dialog is reflected immediately on the map.
    applyRouteStyleToHikeLines() {
      const map = window.map;
      if (!map) return;
      const styleExprs = buildRouteStyleExpressions(getRouteStyleSettings());
      const setPaint = (layerId, props) => {
        if (!map.getLayer(layerId)) return;
        for (const [k, v] of Object.entries(props)) {
          try { map.setPaintProperty(layerId, k, v); } catch {}
        }
      };
      setPaint('hike-lines-border-inactive', {
        'line-color': styleExprs.borderColorExpr,
        'line-width': styleExprs.inactiveBorderWidthExpr,
        'line-opacity': styleExprs.inactiveBorderOpacityExpr,
      });
      setPaint('hike-lines-inactive', {
        'line-color': styleExprs.colorExpr,
        'line-width': styleExprs.inactiveLineWidthExpr,
        'line-opacity': styleExprs.inactiveLineOpacity,
      });
      setPaint('hike-lines-border', {
        'line-color': styleExprs.borderColorExpr,
        'line-width': styleExprs.activeBorderWidthExpr,
        'line-opacity': styleExprs.activeBorderOpacityExpr,
      });
      setPaint('hike-lines', {
        'line-color': styleExprs.colorExpr,
        'line-width': styleExprs.activeLineWidthExpr,
        'line-opacity': styleExprs.activeLineOpacityExpr,
      });
    },

    addToMap({ fitBounds = true } = {}) {
      if (!this.geojson) {
        return;
      }

      // Remove existing layers and sources
      this.removeExistingLayers();

      // Filter features based on active view
      let filteredFeatures = this.geojson.features;
      
      if (this.activeView.type === 'day') {
        // Show only features for the selected day
        filteredFeatures = this.geojson.features.filter(f => {
          const matches = f.properties && f.properties.hikeDayNumber === this.activeView.dayNumber;
          return matches;
        });
      } else if (this.activeView.type === 'route') {
        // Show only features for the selected route
        filteredFeatures = this.geojson.features.filter(f => {
          // Convert both IDs to strings for comparison to handle string/number mismatch
          const featureTrailId = f.properties?.ptv2PlannedTrailId;
          const targetTrailId = this.activeView.trailId;
          const matches = featureTrailId != null && String(featureTrailId) === String(targetTrailId);
          return matches;
        });
      }
      // If type is 'full', show all features (no filtering needed)

      const derivedBounds = this.deriveBoundsFromFeatures(filteredFeatures);
      this.activeBounds = derivedBounds;

      // Sort features by the authoritative day/position order from plannedTrails
      // (same data source as the left panel) so that first/last route detection
      // for start/end markers is always correct regardless of GeoJSON emission order.
      const sortedFeatures = this.sortFeaturesByTrailOrder(filteredFeatures);

      // Filter features by type from the filtered set
      const multiLineStrings = sortedFeatures.filter(f =>
        f.geometry.type === 'MultiLineString'
      );
      const { startMarkers, endMarkers } = this.getStartEndMarkers(sortedFeatures);

      // Collect inactive (dimmed) routes for route and day views — matching hike-planner behavior.
      // Route view: all routes except the selected one.
      // Day view: all routes not belonging to the selected day.
      let inactiveMultiLineStrings = [];
      if (this.activeView.type === 'route') {
        const activeTrailId = String(this.activeView.trailId);
        inactiveMultiLineStrings = this.sortFeaturesByTrailOrder(this.geojson.features).filter(f =>
          f.geometry.type === 'MultiLineString' &&
          (f.properties?.ptv2PlannedTrailId == null ||
            String(f.properties.ptv2PlannedTrailId) !== activeTrailId)
        );
      } else if (this.activeView.type === 'day') {
        inactiveMultiLineStrings = this.sortFeaturesByTrailOrder(this.geojson.features).filter(f =>
          f.geometry.type === 'MultiLineString' &&
          f.properties?.hikeDayNumber !== this.activeView.dayNumber
        );
      }

      // Map zoom levels to icon sizes so the markers scale smoothly but cap at 0.3 after zoom 12.5.
      // Note: avoiding intermediate stops near 12 keeps Mapbox from snapping the size when crossing integer zooms.
      const markerIconSizeExpression = [
        'interpolate',
        ['linear'],
        ['zoom'],
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        0,
        0.1,
        // zoom 0  -> size 0.12 (minimum size when zoomed out)
        9,
        0.2,
        // zoom 12.5 -> size 0.3 (maximum size reached here)
        12,
        0.3,
        // maintain the cap for any higher zoom levels
        22,
        0.5
      ];

      // Add multilinestrings
      if (multiLineStrings.length > 0) {
        // Per-route-type paint properties from the user's customise-route-style
        // settings (localStorage, synced via routeStyleSettingsCloudSync). The
        // re-apply on `route-style-settings:changed` lives in addToMap() — see
        // mounted() for the listener wiring.
        const styleExprs = buildRouteStyleExpressions(getRouteStyleSettings());
        const colorExpression = styleExprs.colorExpr;
        const borderColorExpression = styleExprs.borderColorExpr;

        // Create dash array expression for transport style
        const dashArrayExpression = [
          'case',
          ['==', ['get', 'routeStyle'], 'transport'],
          ['literal', ROUTE_STYLES.transport.dasharray || [1, 0]],
          ['literal', [1, 0]] // solid line for non-transport styles
        ];

        // Add inactive route layers (dimmed) when a specific route is selected — matching hike-planner behavior
        if (inactiveMultiLineStrings.length > 0) {
          window.layerRegistry.addLayer(
            {
              id: 'hike-lines-border-inactive',
              type: 'line',
              source: 'hike-lines-inactive',
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': borderColorExpression,
                'line-width': styleExprs.inactiveBorderWidthExpr,
                'line-opacity': styleExprs.inactiveBorderOpacityExpr,
                'line-dasharray': dashArrayExpression,
              },
            },
            MapLayerCategory.Annotations,
            {
              source: {
                id: 'hike-lines-inactive',
                spec: {
                  type: 'geojson',
                  data: {
                    type: 'FeatureCollection',
                    features: inactiveMultiLineStrings
                  },
                },
              },
              z: 7,
            }
          );

          window.layerRegistry.addLayer(
            {
              id: 'hike-lines-inactive',
              type: 'line',
              source: 'hike-lines-inactive',
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': colorExpression,
                'line-width': styleExprs.inactiveLineWidthExpr,
                'line-opacity': styleExprs.inactiveLineOpacity,
                'line-dasharray': dashArrayExpression,
              },
            },
            MapLayerCategory.Annotations,
            {
              source: {
                id: 'hike-lines-inactive',
                spec: {
                  type: 'geojson',
                  data: {
                    type: 'FeatureCollection',
                    features: inactiveMultiLineStrings
                  },
                },
              },
              z: 8,
            }
          );
        }

        // Add border layer first (wider line with border color) - exclude transport style
        window.layerRegistry.addLayer(
          {
            id: 'hike-lines-border',
            type: 'line',
            source: 'hike-lines',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': borderColorExpression,
              'line-width': styleExprs.activeBorderWidthExpr,
              'line-opacity': styleExprs.activeBorderOpacityExpr,
              'line-dasharray': dashArrayExpression,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-lines',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: multiLineStrings
                },
              },
            },
            z: 9, // Lower z-order so border appears behind main line
          }
        );

        // Add main line layer (thinner line with main color)
        window.layerRegistry.addLayer(
          {
            id: 'hike-lines',
            type: 'line',
            source: 'hike-lines',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': colorExpression,
              'line-width': styleExprs.activeLineWidthExpr,
              'line-opacity': styleExprs.activeLineOpacityExpr,
              'line-dasharray': dashArrayExpression,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-lines',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: multiLineStrings
                },
              },
            },
            z: 10, // Higher z-order so main line appears on top
          }
        );

        // Add distance markers — small dots with the cumulative whole-unit value inside.
        const distanceMarkerFeatures = this.buildDistanceMarkerFeatures(multiLineStrings);
        if (distanceMarkerFeatures.length > 0) {
          const distanceMarkerSource = {
            id: 'hike-lines-distance-markers',
            spec: {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: distanceMarkerFeatures,
              },
            },
          };

          window.layerRegistry.addLayer(
            {
              id: 'hike-lines-distance-markers',
              type: 'symbol',
              source: 'hike-lines-distance-markers',
              minzoom: 12,
              layout: {
                // The chevron sprite is drawn pointing east, so subtract 90°
                // from the geographic bearing to align it with travel direction.
                'icon-image': 'hike-arrow-icon',
                'icon-size': 0.15,
                'icon-rotate': ['-', ['get', 'bearing'], 90],
                'icon-rotation-alignment': 'map',
                'icon-anchor': 'center',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': ['get', 'label'],
                'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
                'text-size': 11,
                'text-anchor': 'left',
                'text-offset': [0.9, 0],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
                'symbol-sort-key': ['get', 'distance'],
              },
              paint: {
                'text-color': '#1f2026',
                'text-halo-color': 'rgba(255,255,255,0.95)',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5,
              },
            },
            MapLayerCategory.Annotations,
            {
              source: distanceMarkerSource,
              z: 13,
            }
          );
        }

        // Route name labels inline along the line, visible when zoomed in.
        window.layerRegistry.addLayer(
          {
            id: 'hike-lines-names',
            type: 'symbol',
            source: 'hike-lines',
            minzoom: 12,
            layout: {
              'symbol-placement': 'line',
              'text-field': ['get', 'name'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-allow-overlap': false,
              'symbol-spacing': 250,
              'text-max-angle': 30,
              'text-pitch-alignment': 'map',
            },
            paint: {
              'text-color': '#333333',
              'text-halo-color': 'rgba(255, 255, 255, 0.8)',
              'text-halo-width': 1.5,
            },
          },
          MapLayerCategory.Annotations,
          {
            z: 12,
          }
        );
      }

      // Add end markers before start markers so start markers can sit above
      if (endMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'hike-markers-end',
            type: 'symbol',
            source: 'hike-markers-end',
            layout: {
              'icon-image': 'hike-control-point-end',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-markers-end',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: endMarkers
                },
              },
            },
            z: 20,
          }
        );
      }

      if (startMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'hike-markers-start',
            type: 'symbol',
            source: 'hike-markers-start',
            layout: {
              'icon-image': 'hike-control-point-start',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom', // keep marker tip fixed while scaling
              'icon-allow-overlap': true,
            },
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-markers-start',
              spec: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: startMarkers
                },
              },
            },
            z: 25,
          }
        );
      }

      // Fit the camera to the active view's extent. When fitBounds is false
      // (e.g. re-adding layers after a base-map style switch) this returns null
      // so the user's current map position and zoom are preserved.
      const boundsToFit = computeHikeMapFitBounds({
        fitBounds,
        filteredFeatures,
        activeViewType: this.activeView.type,
        bbox: this.bbox,
        allFeatures: this.geojson?.features,
        computeBoundsFromFeatures: (features) => this.computeBoundsFromFeatures(features),
      });
      if (boundsToFit) {
        window.map.fitBounds(boundsToFit, { padding: 150 });
      }

      // Add hike highlights to the map
      this.addHighlightsToMap();

      this.loading = false;
    },
    onTrailSelected(trail, index) {
      this.selectedTrailIndex = index;
      // Filter geojson to show only the selected trail
      if (this.geojson && this.geojson.features) {
        // Find features that belong to this trail based on ptv2PlannedTrailId
        const trailId = trail.id;
        const filteredFeatures = this.geojson.features.filter((feature) => {
          return feature.properties && feature.properties.ptv2PlannedTrailId === trailId;
        });

        if (filteredFeatures.length > 0) {
          // Update the map to highlight the selected trail
          const bounds = new mapboxgl.LngLatBounds();
          filteredFeatures.forEach((feature) => {
            if (feature.geometry) {
              if (feature.geometry.type === 'MultiLineString') {
                feature.geometry.coordinates.forEach((lineString) => {
                  lineString.forEach((coord) => {
                    if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                      bounds.extend([coord[0], coord[1]]);
                    }
                  });
                });
              } else if (feature.geometry.type === 'Point') {
                const coord = feature.geometry.coordinates;
                if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                  bounds.extend([coord[0], coord[1]]);
                }
              }
            }
          });
          window.map.fitBounds(bounds, { padding: 150 });
        }
      }

      if (this.isMobile) {
        this.toggleMobileTrails(false);
      }
    },
    maybeEnableEditButton(hike) {
      if (!hike || !window.user) {
        return;
      }

      const currentUserFirebaseId = window.user.firebase_user_id;
      const hikeOwnerFirebaseId = hike.user?.firebaseUserId;

      if (!currentUserFirebaseId || !hikeOwnerFirebaseId || currentUserFirebaseId !== hikeOwnerFirebaseId) {
        return;
      }

      const shareableUrl = hike.shareableUrl;

      if (!shareableUrl) {
        return;
      }

      let pathname;

      try {
        pathname = new URL(shareableUrl, window.location.origin).pathname;
      } catch (error) {
        pathname = shareableUrl;
      }

      this.editHikeUrl = `${pathname.replace(/\/?$/, '')}/edit`;
    },
    onViewChanged(viewData) {
      this.activeView = viewData;
      // Re-render the map with the new filter
      this.addToMap();
    },
    onShowOptions(optionsData) {
      if (!optionsData) {
        return;
      }

      const { type, action } = optionsData;

      if (action === 'directions') {
        this.handleDrivingDirections(optionsData);
        return;
      }

      if (action === 'gpx') {
        this.handleGpxExport(optionsData);
        return;
      }

      if (action === 'print') {
        this.handlePrintMap(optionsData);
        return;
      }

      if (action === 'gps-device') {
        return;
      }

      if (action === 'edit') {
        this.handleEditInPlanner(optionsData);
        return;
      }

      // Fallback for other options until implemented
      console.log('Show options for:', optionsData);
    },
    handleGpxExport({ type, data }) {
      const context = this.normalizeGpxContext(type, data);
      if (!context) {
        console.warn('Unsupported GPX export context', { type, data });
        return;
      }

      const { lineFeatures, fileName, metadata } = context;

      if (!lineFeatures || lineFeatures.length === 0) {
        window.alert('No GPX data is available for this selection yet.');
        return;
      }

      const featureCollection = {
        type: 'FeatureCollection',
        features: lineFeatures,
      };

      try {
        const gpx = geojsonToGpx(featureCollection, null, {
          ...metadata,
          outputType: 'rte',
          routeName: metadata?.metadata?.name || this.hikeName || 'Hike',
          mergeRoutesByProperty: type === 'full-action' ? 'hikeDayNumber' : undefined
        });
        this.downloadGpxFile(gpx, fileName);
      } catch (error) {
        console.error('Error creating GPX export:', error);
        window.alert('We could not generate the GPX file. Please try again.');
      }
    },
    handleDrivingDirections({ type, data }) {
      let coordinate = null;

      if (type === 'route-action') {
        const dayNumber = this.getTrailDayNumber(data);
        coordinate = this.findStartCoordinateForTrail(data, dayNumber);
      } else if (type === 'day-action') {
        coordinate = this.findStartCoordinateForDay(data);
      } else if (type === 'full-action') {
        coordinate = this.findStartCoordinateForFull();
      }

      if (!coordinate) {
        console.warn('Unable to determine start coordinate for driving directions', { type, data });
        return;
      }

      this.openGoogleMapsDirections(coordinate.lat, coordinate.lon);
    },
    extractTrailId(trail) {
      if (!trail) {
        return null;
      }

      if (trail.ptv2PlannedTrail && trail.ptv2PlannedTrail.id != null) {
        return trail.ptv2PlannedTrail.id;
      }

      if (trail.id != null) {
        return trail.id;
      }

      return null;
    },
    getTrailDayNumber(trail) {
      if (!trail) {
        return null;
      }

      const candidates = [trail.day, trail.hikeDayNumber, trail.ptv2PlannedTrail?.day];

      for (const candidate of candidates) {
        if (candidate == null) {
          continue;
        }

        const value = Number(candidate);
        if (!Number.isNaN(value)) {
          return value;
        }
      }

      return null;
    },
    getRouteDayNumber(trailId) {
      // Find the trail in plannedTrails by ID
      const trail = this.plannedTrails.find(t => {
        const id = this.extractTrailId(t);
        return id != null && String(id) === String(trailId);
      });

      if (trail) {
        return this.getTrailDayNumber(trail);
      }

      // Fallback: check geojson features for hikeDayNumber
      if (this.geojson && Array.isArray(this.geojson.features)) {
        const feature = this.geojson.features.find(f =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(trailId)
        );

        if (feature?.properties?.hikeDayNumber != null) {
          return feature.properties.hikeDayNumber;
        }
      }

      return null;
    },
    getTrailOrderValue(trail) {
      if (!trail) {
        return Number.POSITIVE_INFINITY;
      }

      if (trail.position != null) {
        const value = Number(trail.position);
        if (!Number.isNaN(value)) {
          return value;
        }
      }

      if (trail.sortPosition != null) {
        const value = Number(trail.sortPosition);
        if (!Number.isNaN(value)) {
          return value;
        }
      }

      const trailId = this.extractTrailId(trail);
      if (trailId != null && Array.isArray(this.plannedTrails)) {
        const matchIndex = this.plannedTrails.findIndex((candidate) => {
          const candidateId = this.extractTrailId(candidate);
          return candidateId != null && String(candidateId) === String(trailId);
        });

        if (matchIndex !== -1) {
          return matchIndex;
        }
      }

      return Number.POSITIVE_INFINITY;
    },
    findStartCoordinateForTrail(trail, dayNumber = null) {
      const trailId = this.extractTrailId(trail);
      if (trailId == null) {
        return null;
      }

      let normalizedDay = dayNumber;
      if (normalizedDay != null) {
        const numericDay = Number(normalizedDay);
        normalizedDay = Number.isNaN(numericDay) ? null : numericDay;
      }

      const marker = this.findMarkerInAllFeatures(trailId, 'start', normalizedDay);
      if (!marker?.geometry?.coordinates) {
        return null;
      }

      const [lon, lat] = marker.geometry.coordinates;
      const numericLat = Number(lat);
      const numericLon = Number(lon);

      if (Number.isNaN(numericLat) || Number.isNaN(numericLon)) {
        return null;
      }

      return { lat: numericLat, lon: numericLon };
    },
    findStartCoordinateForDay(dayData) {
      if (!dayData || !Array.isArray(dayData.trails) || dayData.trails.length === 0) {
        return null;
      }

      const sortedTrails = [...dayData.trails].sort((a, b) => this.getTrailOrderValue(a) - this.getTrailOrderValue(b));
      const firstTrail = sortedTrails[0];
      if (!firstTrail) {
        return null;
      }

      let dayNumber = null;
      if (dayData.dayNumber != null) {
        const numericDay = Number(dayData.dayNumber);
        if (!Number.isNaN(numericDay)) {
          dayNumber = numericDay;
        }
      } else {
        dayNumber = this.getTrailDayNumber(firstTrail);
      }

      return this.findStartCoordinateForTrail(firstTrail, dayNumber);
    },
    findStartCoordinateForFull() {
      if (!Array.isArray(this.plannedTrails) || this.plannedTrails.length === 0) {
        return null;
      }

      const sortedTrails = [...this.plannedTrails].sort((a, b) => {
        const dayA = this.getTrailDayNumber(a);
        const dayB = this.getTrailDayNumber(b);

        if (dayA != null && dayB != null && dayA !== dayB) {
          return dayA - dayB;
        }

        if (dayA == null && dayB != null) {
          return 1;
        }

        if (dayA != null && dayB == null) {
          return -1;
        }

        return this.getTrailOrderValue(a) - this.getTrailOrderValue(b);
      });

      const firstTrail = sortedTrails[0];
      if (!firstTrail) {
        return null;
      }

      const dayNumber = this.getTrailDayNumber(firstTrail);
      return this.findStartCoordinateForTrail(firstTrail, dayNumber);
    },
    openGoogleMapsDirections(lat, lon) {
      if (lat == null || lon == null) {
        console.warn('Missing coordinate for driving directions', { lat, lon });
        return;
      }

      const numericLat = Number(lat);
      const numericLon = Number(lon);

      if (Number.isNaN(numericLat) || Number.isNaN(numericLon)) {
        console.warn('Invalid coordinate for driving directions', { lat, lon });
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      const destination = encodeURIComponent(`${numericLat},${numericLon}`);
      const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
      window.open(url, '_blank', 'noopener');
    },
    handlePrintMap({ type, data }) {
      const trailIds = this.resolvePrintTrailIds(type, data);

      if (trailIds.length === 0) {
        window.alert('No printable data found for this selection.');
        return;
      }

      if (!openPrintMapUtil(trailIds)) {
        window.alert('Unable to open the print map page.');
      }
    },
    handleEditInPlanner({ type, data }) {
      console.log('handleEditInPlanner called', { type, data });

      // Collect route features based on the context (route, day, or full)
      let routeFeatures = [];

      if (type === 'route-action') {
        // Single route - collect as single merged feature per route
        routeFeatures = this.collectMergedFeaturesForRoute(data);
        console.log('Route action - collected features:', routeFeatures.length);
      } else if (type === 'day-action') {
        // Day routes - collect as single merged feature per route
        routeFeatures = this.collectMergedFeaturesForDay(data.dayNumber);
        console.log('Day action - collected features:', routeFeatures.length);
      } else if (type === 'full-action') {
        // Full hike - collect as single merged feature per route
        routeFeatures = this.collectMergedFeaturesForFull();
        console.log('Full action - collected features:', routeFeatures.length);
      }

      if (!routeFeatures || routeFeatures.length === 0) {
        console.error('No route features found');
        window.alert('No route data available for editing.');
        return;
      }

      // Helper to find trail metadata from plannedTrails
      const findTrailMetadata = (ptv2PlannedTrailId) => {
        if (!ptv2PlannedTrailId || !this.plannedTrails) return null;
        const idString = String(ptv2PlannedTrailId);
        return this.plannedTrails.find(t => {
          const trailId = t.ptv2PlannedTrail?.id ?? t.id;
          return trailId != null && String(trailId) === idString;
        });
      };

      // Create feature collection with merged LineString per route, including all metadata
      const featureCollection = {
        type: 'FeatureCollection',
        features: routeFeatures.map(f => {
          const ptv2PlannedTrailId = f.properties?.ptv2PlannedTrailId;
          const trailData = findTrailMetadata(ptv2PlannedTrailId);
          const ptv2PlannedTrail = trailData?.ptv2PlannedTrail || trailData;

          // Get the title/name
          const name = ptv2PlannedTrail?.title || trailData?.title || null;

          // Only preserve route references when the current user owns the hike.
          // For hikes the user doesn't own (e.g. official HiiKER hikes), strip
          // reference metadata so edits in the planner create independent copies
          // rather than updating the original routes.
          const referencedHikes = ptv2PlannedTrail?.hikes || [];
          const isReference = this.isHikeOwner && referencedHikes.length > 0;

          return {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: f.geometry.coordinates
            },
            properties: {
              hikeDayNumber: trailData?.day ?? f.properties?.hikeDayNumber,
              routeStyle: ptv2PlannedTrail?.routeStyle || f.properties?.routeStyle,
              ptv2PlannedTrailId: isReference ? ptv2PlannedTrailId : undefined,
              name: name,
              isReference: isReference,
              referencedHikes: isReference ? referencedHikes : undefined,
              position: trailData?.position
            }
          };
        })
      };

      // Include hike name
      if (this.hikeName) {
        featureCollection.hikeName = this.hikeName;
      }

      // Include hikeDays data for day names/descriptions
      if (this.hikeDays && this.hikeDays.length > 0) {
        featureCollection.hikeDays = this.hikeDays.map(day => ({
          dayNumber: day.dayNumber,
          name: day.name || '',
          description: day.description || ''
        }));
      }

      // Include hike highlights
      if (this.hikeHighlights && this.hikeHighlights.length > 0) {
        featureCollection.hikeHighlights = this.hikeHighlights.map(highlight => ({
          type: highlight.type,
          title: highlight.title || '',
          description: highlight.description || '',
          coordinates: highlight.coordinates,
          dayNumber: highlight.dayNumber,
          images: highlight.images || []
        }));
      }

      console.log('FeatureCollection to store:', featureCollection);
      console.log('hikeName:', featureCollection.hikeName);
      console.log('hikeDays:', featureCollection.hikeDays);
      console.log('hikeHighlights:', featureCollection.hikeHighlights);

      // Store in localStorage
      try {
        localStorage.setItem('hikerPlannerImport', JSON.stringify(featureCollection));
        console.log('Stored in localStorage, navigating to /hike-planner');

        // Navigate to the hike planner
        window.location.href = '/hike-planner';
      } catch (error) {
        console.error('Error storing route data:', error);
        window.alert('Unable to prepare route for editing. Please try again.');
      }
    },
    normalizeGpxContext(type, data) {
      const baseMetadata = {
        creator: 'Hiiker',
        metadata: {},
      };

      if (type === 'route-action') {
        const lineFeatures = this.collectLineFeaturesForRoute(data);
        const title = this.resolveTrailTitle(data) || this.hikeName || 'Route';
        const createdAt = this.resolveTrailCreatedAt(data);

        if (title) baseMetadata.metadata.name = title;
        if (createdAt) baseMetadata.metadata.time = createdAt;

        return {
          lineFeatures,
          fileName: this.composeGpxFileName(title || 'route'),
          metadata: baseMetadata,
        };
      }

      if (type === 'day-action') {
        const dayNumber = data?.dayNumber;
        const lineFeatures = this.collectLineFeaturesForDay(dayNumber);

        const dayName = this.resolveDayName(dayNumber) || `Day ${dayNumber ?? ''}`.trim();
        const createdAt = this.resolveFirstTrailCreatedAt(data?.trails);

        const title = [this.hikeName, dayName].filter(Boolean).join(' - ') || dayName || this.hikeName || 'Hike Day';
        if (title) baseMetadata.metadata.name = title;
        if (createdAt) baseMetadata.metadata.time = createdAt;

        return {
          lineFeatures,
          fileName: this.composeGpxFileName(title),
          metadata: baseMetadata,
        };
      }

      if (type === 'full-action') {
        const lineFeatures = this.collectLineFeaturesForFull();
        const title = this.hikeName || 'Hike';
        const createdAt = this.resolveFirstTrailCreatedAt(this.plannedTrails);

        if (title) baseMetadata.metadata.name = title;
        if (createdAt) baseMetadata.metadata.time = createdAt;

        return {
          lineFeatures,
          fileName: this.composeGpxFileName(title || 'hike'),
          metadata: baseMetadata,
        };
      }

      return null;
    },
    collectMergedFeaturesForRoute(trail) {
      const trailId = this.extractTrailId(trail);
      if (trailId == null) {
        return [];
      }

      const idString = String(trailId);
      const lineFeatures = this.collectLineFeatures(feature => {
        const featureTrailId = feature?.properties?.ptv2PlannedTrailId;
        return featureTrailId != null && String(featureTrailId) === idString;
      });

      // Merge all linestrings for this route into a single linestring
      return this.mergeLineStringsByRoute(lineFeatures);
    },
    collectMergedFeaturesForDay(dayNumber) {
      if (dayNumber == null) {
        return [];
      }

      const numericDay = Number(dayNumber);
      if (Number.isNaN(numericDay)) {
        return [];
      }

      const lineFeatures = this.collectLineFeatures(feature => feature?.properties?.hikeDayNumber === numericDay);

      // Merge all linestrings for each route in this day
      return this.mergeLineStringsByRoute(lineFeatures);
    },
    collectMergedFeaturesForFull() {
      const lineFeatures = this.collectLineFeatures(() => true);

      // Merge all linestrings for each route in the hike
      return this.mergeLineStringsByRoute(lineFeatures);
    },
    mergeLineStringsByRoute(lineFeatures) {
      if (!lineFeatures || lineFeatures.length === 0) {
        return [];
      }

      // Group features by route ID
      const routeGroups = {};

      lineFeatures.forEach(feature => {
        const routeId = feature.properties?.ptv2PlannedTrailId;
        if (routeId == null) {
          return;
        }

        const key = String(routeId);
        if (!routeGroups[key]) {
          routeGroups[key] = {
            coordinates: [],
            properties: feature.properties
          };
        }

        // Concatenate all coordinates for this route
        routeGroups[key].coordinates.push(...feature.geometry.coordinates);
      });

      // Convert grouped routes back to feature array
      return Object.values(routeGroups).map(group => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: group.coordinates
        },
        properties: group.properties
      }));
    },
    collectLineFeaturesForRoute(trail) {
      const trailId = this.extractTrailId(trail);
      if (trailId == null) {
        return [];
      }

      const idString = String(trailId);
      return this.collectLineFeatures(feature => {
        const featureTrailId = feature?.properties?.ptv2PlannedTrailId;
        return featureTrailId != null && String(featureTrailId) === idString;
      });
    },
    collectLineFeaturesForDay(dayNumber) {
      if (dayNumber == null) {
        return [];
      }

      const numericDay = Number(dayNumber);
      if (Number.isNaN(numericDay)) {
        return [];
      }

      return this.collectLineFeatures(feature => feature?.properties?.hikeDayNumber === numericDay);
    },
    collectLineFeaturesForFull() {
      return this.collectLineFeatures(() => true);
    },
    collectLineFeatures(predicate) {
      if (!this.geojson || !Array.isArray(this.geojson.features)) {
        return [];
      }

      const results = [];

      this.geojson.features.forEach(feature => {
        if (!predicate(feature) || !feature?.geometry) {
          return;
        }

        if (feature.geometry.type === 'LineString') {
          if (Array.isArray(feature.geometry.coordinates) && feature.geometry.coordinates.length > 1) {
            results.push({
              type: 'Feature',
              properties: feature.properties ? { ...feature.properties } : {},
              geometry: {
                type: 'LineString',
                coordinates: feature.geometry.coordinates,
              },
            });
          }
        } else if (feature.geometry.type === 'MultiLineString') {
          const lines = feature.geometry.coordinates || [];
          lines.forEach(line => {
            if (Array.isArray(line) && line.length > 1) {
              results.push({
                type: 'Feature',
                properties: feature.properties ? { ...feature.properties } : {},
                geometry: {
                  type: 'LineString',
                  coordinates: line,
                },
              });
            }
          });
        }
      });

      return results;
    },
    resolveTrailTitle(trail) {
      if (!trail) return null;
      if (trail.ptv2PlannedTrail?.title) return trail.ptv2PlannedTrail.title;
      if (trail.title) return trail.title;
      return null;
    },
    resolveTrailCreatedAt(trail) {
      if (!trail) return null;
      return trail.ptv2PlannedTrail?.createdAt || trail.createdAt || null;
    },
    resolveDayName(dayNumber) {
      if (dayNumber == null || !Array.isArray(this.hikeDays)) {
        return null;
      }

      const numericDay = Number(dayNumber);
      const match = this.hikeDays.find(day => Number(day.dayNumber) === numericDay);
      return match?.name || (Number.isNaN(numericDay) ? null : `Day ${numericDay}`);
    },
    resolveFirstTrailCreatedAt(trails) {
      if (!Array.isArray(trails) || trails.length === 0) {
        return null;
      }

      for (const trail of trails) {
        const createdAt = this.resolveTrailCreatedAt(trail);
        if (createdAt) {
          return createdAt;
        }
      }

      return null;
    },
    composeGpxFileName(label) {
      const base = (label || this.hikeName || 'hiiker').toString();
      const slug = base
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'hiiker';

      return `${slug}.gpx`;
    },
    downloadGpxFile(gpxContent, fileName) {
      const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName || 'hiiker.gpx';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      URL.revokeObjectURL(url);
    },
    resolvePrintTrailIds(type, data) {
      if (type === 'route-action') {
        const id = this.extractTrailId(data);
        return id != null ? [String(id)] : [];
      }

      if (type === 'day-action') {
        return this.collectTrailIds(data?.trails);
      }

      if (type === 'full-action') {
        return this.collectTrailIds(this.plannedTrails);
      }

      return [];
    },
    collectTrailIds(trails) {
      if (!Array.isArray(trails)) {
        return [];
      }

      const ids = new Set();

      trails.forEach(trail => {
        const id = this.extractTrailId(trail);
        if (id != null) {
          ids.add(String(id));
        }
      });

      return Array.from(ids);
    },
    computeBoundsFromFeatures(features) {
      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;

      features.forEach((feature) => {
        if (!feature?.geometry) {
          return;
        }

        if (feature.geometry.type === 'MultiLineString') {
          feature.geometry.coordinates.forEach((lineString) => {
            lineString.forEach((coord) => {
              if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                bounds.extend([coord[0], coord[1]]);
                hasBounds = true;
              }
            });
          });
        } else if (feature.geometry.type === 'Point') {
          const coord = feature.geometry.coordinates;
          if (!isNaN(coord[0]) && !isNaN(coord[1])) {
            bounds.extend([coord[0], coord[1]]);
            hasBounds = true;
          }
        }
      });

      return hasBounds ? bounds : null;
    },
    removeExistingLayers() {
      // Remove old layers and sources if they exist
      const layersToRemove = ['line', 'hike-lines', 'hike-lines-border', 'hike-lines-inactive', 'hike-lines-border-inactive', 'hike-lines-names', 'hike-lines-distance-markers', 'hike-markers', 'hike-markers-start', 'hike-markers-end'];

      // First, remove all layers
      layersToRemove.forEach(layerId => {
        if (window.map.getLayer(layerId)) {
          window.map.removeLayer(layerId);
        }
      });

      // Then, remove all sources (after layers are removed)
      const sourcesToRemove = ['line', 'hike-lines', 'hike-lines-inactive', 'hike-lines-distance-markers', 'hike-markers', 'hike-markers-start', 'hike-markers-end'];
      sourcesToRemove.forEach(sourceId => {
        if (window.map.getSource(sourceId)) {
          window.map.removeSource(sourceId);
        }
      });
    },
    handleHighlightToggle(enabled) {
      const shouldShow = !!enabled;
      if (this.showHighlights === shouldShow) {
        return;
      }
      this.showHighlights = shouldShow;
      if (this.showHighlights) {
        this.addHighlightsToMap();
      } else {
        this.removeHighlightsFromMap();
      }
    },
    addHighlightsToMap() {
      if (!this.showHighlights) {
        return;
      }
      if (!this.hikeHighlights || this.hikeHighlights.length === 0) {
        return;
      }
      if (!window.map || !window.layerRegistry) {
        return;
      }

      // Remove all existing highlights first to ensure clean state
      this.removeHighlightsFromMap();

      // Filter highlights based on active view
      let filteredHighlights = this.hikeHighlights;

      if (this.activeView.type === 'day') {
        // Show only highlights for the selected day
        filteredHighlights = this.hikeHighlights.filter(h =>
          h.dayNumber === this.activeView.dayNumber
        );
      } else if (this.activeView.type === 'route') {
        // Show highlights for the route's parent day
        const routeDayNumber = this.getRouteDayNumber(this.activeView.trailId);
        if (routeDayNumber != null) {
          filteredHighlights = this.hikeHighlights.filter(h =>
            h.dayNumber === routeDayNumber
          );
        }
      }
      // If type is 'full', show all highlights (no filtering needed)

      // Add each highlight as a marker on the map
      filteredHighlights.forEach((highlight) => {
        const id = `highlight-${highlight.id || highlight.clientId}`;

        // Get the icon name from POIUtility
        let iconName = 'highlight_icon_custom'; // default
        try {
          const poiUtil = POIUtility.fromString(highlight.type);
          iconName = poiUtil.hikeHighlightIcon();
        } catch (e) {
          console.warn(`Unknown POI type: ${highlight.type}, using default icon`);
        }

        // Create GeoJSON source for the highlight
        const source = {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: highlight.coordinates
            },
            properties: {
              id: highlight.id,
              type: highlight.type,
              title: highlight.title,
              description: highlight.description,
              dayNumber: highlight.dayNumber
            }
          }
        };

        // Create symbol layer for the highlight
        const layer = {
          id: id,
          type: 'symbol',
          source: id,
          minzoom: 10,
          layout: {
            'icon-image': iconName,
            'icon-size': 0.3,
            'icon-allow-overlap': true,
            'text-field': highlight.title,
            'text-font': ['Roboto Regular', 'Arial Unicode MS Regular'],
            'text-size': 12,
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-optional': true
          },
          paint: {
            'text-color': '#333',
            'text-halo-color': '#fff',
            'text-halo-width': 2
          }
        };

        // Add layer using LayerRegistry for proper layer ordering
        window.layerRegistry.addLayer(
          layer,
          MapLayerCategory.Annotations,
          {
            source: {
              id: id,
              spec: source
            },
            z: 30, // Higher z-order than routes and markers
            replace: true // Replace if it already exists
          }
        );

        // Add click listener for the highlight
        window.map.on('click', id, (e) => {
          if (e.features && e.features.length > 0) {
            const feature = e.features[0];
            const props = feature.properties;
            
            // Show highlight detail dialog
            if (window.showHighlightDetail) {
              window.showHighlightDetail({
                id: props.id,
                clientId: highlight.clientId || highlight.id,
                type: props.type,
                title: props.title,
                description: props.description,
                dayNumber: props.dayNumber,
                coordinates: feature.geometry.coordinates,
                images: highlight.images || [],
                mediaAttachments: highlight.mediaAttachments || [],
              });
            }
            
            // Prevent event from bubbling
            e.originalEvent.stopPropagation();
          }
        });

        // Change cursor on hover
        window.map.on('mouseenter', id, () => {
          window.map.getCanvas().style.cursor = 'pointer';
        });

        window.map.on('mouseleave', id, () => {
          window.map.getCanvas().style.cursor = '';
        });
      });
    },
    removeHighlightsFromMap() {
      if (!this.hikeHighlights || this.hikeHighlights.length === 0) {
        return;
      }
      if (!window.map) {
        return;
      }

      // Remove all highlight layers
      this.hikeHighlights.forEach(highlight => {
        const id = `highlight-${highlight.id || highlight.clientId}`;

        // Remove event listeners
        window.map.off('click', id);
        window.map.off('mouseenter', id);
        window.map.off('mouseleave', id);

        // Remove layer and source
        if (window.map.getLayer(id)) {
          window.map.removeLayer(id);
        }
        if (window.map.getSource(id)) {
          window.map.removeSource(id);
        }
      });
    },
    createNewHike() {
      let mapCenter = window.map.getCenter();
      let lat = mapCenter.lat;
      let lon = mapCenter.lng;
      let zoom = Math.round(window.map.getZoom());
      window.location.href = `/hike-planner?lat=${lat}&lon=${lon}&zoom=${zoom}`;
    },
    async reportIssue() {
      if (!window.hike_id) {
        window.alert('Unable to report issue: Hike ID not found');
        return;
      }

      try {
        // Construct the report issue URL based on the current page URL
        // Replace /map with /report_issue
        const currentPath = window.location.pathname;
        const reportUrl = currentPath.replace(/\/map$/, '/report_issue');

        const response = await fetch(reportUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        });

        const data = await response.json();

        if (data.success) {
          window.alert('Thank you for reporting this issue. Our team has been notified.');
        } else {
          window.alert('Failed to report issue. Please try again.');
        }
      } catch (error) {
        console.error('Error reporting issue:', error);
        window.alert('Failed to report issue. Please try again.');
      }
    },
    editHike() {
      if (this.editHikeUrl) {
        window.location.href = this.editHikeUrl;
      } else {
        console.warn('Edit hike URL not available');
      }
    },
    openWaypoints() {
      const mo = document.getElementById('map-options-dialog');
      if (mo) Modal.getOrCreateInstance(mo).hide();

      if (window.user?.firebase_user_id) {
        const el = document.getElementById('waypoint-options-dialog');
        if (el) Modal.getOrCreateInstance(el).show();
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
      }
    },
    showGoTo() {
      const el = document.getElementById('go-to-location-dialog');
      if (el) { Modal.getOrCreateInstance(el).show(); }
      const mo = document.getElementById('map-options-dialog'); if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    async showMeasurement() {
      const isPro = await RevenueCatWrapper.isProOrProPlus();
      if (!isPro) { window.location.href = '/purchases'; return; }

      // Set the measureable GeoJSON data for the measurement tool
      if (this.geojson) {
        this.$store.dispatch('hikeMeasurement/setMeasureableGeoJson', this.geojson);
      }

      // Start measurement mode
      this.$store.dispatch('hikeMeasurement/startMeasurementMode');

      const mo = document.getElementById('map-options-dialog'); if (mo) Modal.getOrCreateInstance(mo).hide();
    },
    openPrintMap() {
      // When called from MapOptionsDialog, print the entire hike
      const trailIds = this.collectTrailIds(this.plannedTrails);
      if (trailIds.length === 0) {
        window.alert('No printable data found.');
        return;
      }

      if (!openPrintMapUtil(trailIds)) {
        window.alert('Unable to open the print map page.');
      }
    },
    updateHoverMarker(coordinate) {
      // Remove existing hover marker if it exists
      if (this.hoverMarker) {
        this.hoverMarker.remove();
        this.hoverMarker = null;
      }

      // If coordinate is null (mouse left the chart), don't add a marker
      if (!coordinate || !window.map) {
        return;
      }

      // Create a new marker at the hover coordinate
      const el = document.createElement('div');
      el.className = 'hover-marker';
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.backgroundColor = '#4CAF50';
      el.style.border = '2px solid white';
      el.style.borderRadius = '50%';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

      this.hoverMarker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat(coordinate)
        .addTo(window.map);
    },
  },
  beforeDestroy() {
    // Clean up event listener when component is destroyed
    window.removeEventListener('resize', this.resizeMap);
    window.removeEventListener('resize', this.handleViewportChange);
    emitter.off('map-style-loaded');
    emitter.off('hike-highlights:toggle', this.handleHighlightToggle);
    emitter.off('route-style-settings:changed', this.applyRouteStyleToHikeLines);

    // Clean up map resources
    if (window.map) {
      // Remove mouse move listener
      if (this.mouseMoveHandler) {
        window.map.off('mousemove', this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }

      // Remove highlights
      this.removeHighlightsFromMap();

      // Remove sources and layers
      this.removeExistingLayers();

      // Remove the map instance
      window.map.remove();
      window.map = null;
    }
  },
};
</script>

<style scoped>
.mapboxgl-map{
  font-family: Montserrat, Helvetica, Arial, sans-serif;
}

.map-container {
  width: 100%;
  height: 84svh;
  position: relative;
}

/* Ensure the map controls are above the floating panel */
.map-container :deep(.mapboxgl-ctrl-top-left),
.map-container :deep(.mapboxgl-ctrl-bottom-left),
.map-container :deep(.mapboxgl-ctrl-top-right),
.map-container :deep(.mapboxgl-ctrl-bottom-right) {
  z-index: 1001;
}


.bottom-controls-container > * {
  pointer-events: auto;
}

/* Coords bar styling - matches global app.scss */
.coordsbar {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 6px;
  font-size: .8em;
  background: rgb(27 27 29 / 90%);
  bottom: 0;
  left: 0;
  right: 0;
  width: fit-content;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  z-index: 100;
  color: white;
}

.coordsbar .material-icons {
  font-size: 12px;
}

.mobile-action-bar {
  display: none;
}

.mobile-action-btn {
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.mobile-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
}

.mobile-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 27, 33, 0.98);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -10px 32px rgba(0, 0, 0, 0.4);
  z-index: 1110;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 75vh;
}

.mobile-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.mobile-sheet__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.mobile-sheet__close {
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.mobile-sheet__close:hover {
  background: rgba(255, 255, 255, 0.18);
}

.mobile-sheet__content {
  flex: 1;
  overflow-y: auto;
}

.mobile-sheet__panel {
  position: static;
  width: 100%;
  max-height: none;
}

.mobile-sheet--routes {
  padding-top: 8px;
}

.mobile-sheet--elevation .mobile-sheet__content {
  padding: 0 16px 20px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.mobile-sheet-enter-active,
.mobile-sheet-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.mobile-sheet-enter-from,
.mobile-sheet-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

@media (max-width: 768px) {

  .map-container {
    height: 88svh;
  }

  .coordsbar {
    /* On smaller screens, anchor to the bottom-left */
    left: 5px;
    right: auto;
    bottom: 78px;
    margin: 0;
    border-radius: 4px;
    font-size: 0.75em;
  }

  .mobile-action-bar {
    position: fixed;
    bottom: 46px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    background: rgba(26, 27, 33, 0.95);
    border-radius: 999px;
    padding: 8px 10px;
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    color: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(12px);
  }

  .mobile-action-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 11px;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    font-size: 13px;
    font-weight: 500;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .mobile-action-btn .material-icons {
    font-size: 18px;
  }

  .mobile-action-btn.active {
    background: rgba(255, 223, 70, 0.25);
    color: #fff;
  }

  .mobile-action-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .mobile-sheet :deep(.hike-trails-panel) {
    position: static;
    width: 100%;
    max-height: none;
    box-shadow: none;
    background: transparent;
  }

  .mobile-sheet :deep(.panel-content) {
    max-height: calc(75vh - 64px);
  }

  .mobile-sheet :deep(.trail-list) {
    padding-bottom: 32px;
  }

  .mobile-sheet :deep(.panel-footer) {
    background: transparent;
  }

  .mobile-sheet--routes :deep(.hike-trails-panel) {
    background: transparent;
  }

  .mobile-sheet--routes :deep(.panel-header) {
    background: transparent;
    padding-left: 20px;
    padding-right: 20px;
  }

  .mobile-sheet--routes :deep(.trail-item) {
    padding-left: 20px;
    padding-right: 20px;
  }

  .mobile-sheet--elevation :deep(.elevation-profile-container) {
    position: static;
    background: transparent;
    box-shadow: none;
    border: none;
  }

  .mobile-sheet--elevation :deep(.profile-header) {
    display: none;
  }

  .mobile-sheet--elevation :deep(.profile-content) {
    height: 100%;
  }
}

</style>
